import React, { SyntheticEvent, useState } from 'react';
import styled from 'styled-components';
import { up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { Focus } from '@fenderdigital/ui/utils/css';
import CompletedIcon from '@fenderdigital/ui/Icons/CheckCircleFill';
import IncompleteIcon from '@fenderdigital/ui/Icons/CheckCircle';
import FreemiumCheckMark from '@fenderdigital/ui/Icons/FreemiumCheckMark';
import { Course, Lesson } from '@fenderdigital/api-definitions/play';
import Spacer from '@fenderdigital/ui/Spacer';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { useSelector, useDispatch } from 'react-redux';
import capitalize from 'lodash/capitalize';
import { RootState } from '../../redux-types/root.redux-types';
import { getLessonsPayload } from '../../utils/progress-utils';
import { getPercentCompleted, getLessonPercent } from '../ProgressList/progress-list-utils';
import { updateLessonCompletedProgress } from '../../actions/my-progress';
import { MarkCompleteData } from '../MarkComplete/MarkComplete';
import { trackEvent, EventNames } from '../../lib/analytics';

interface CardMenuProps {
  courseData?: Course;
  lessonData?: Lesson;
  onlyIcons?: boolean;
  markCompleteData?: MarkCompleteData;
  courseId?: string;
  testId: string;
  isCourseHeader?: boolean;
  isSong?: boolean;
}

interface ButtonProps {
  isSong?: boolean;
  isComplete?: boolean;
}

const IconButton = styled.button`
  cursor: pointer;
  min-width: 20px;
  min-height: 44px;
  padding: 0px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border: none;

  ${Focus}

  ${up('tablet-portrait')} {
    margin-right: 16px;
  }
`;

const Button = styled.button<ButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: ${({ isSong }): string => (isSong ? 'none' : '1px solid black')};
  min-width: 158px;
  min-height: 36px;
  border-radius: 20px;
  font-family: ${({ theme }): string => theme.fonts.futuraDemi};
  background-color: ${({ isComplete }): string => (isComplete ? 'black' : 'white')};
  color: ${({ isComplete }): string => (isComplete ? 'white' : 'black')};
  overflow: hidden;
  position: relative;

  ${Focus}
`;

const ButtonContainer = styled.div`
  .complete {
    span {
      padding-top: 0.25rem;
      animation: check-icon-move-in;
      animation-timing-function: ease-in-out;
      animation-duration: 1000ms;
      animation-fill-mode: forwards;
      position: absolute;

      @keyframes check-icon-move-in {
        0% {
          transform: translate(0px, 50px);
        }
        26% {
          transform: translate(0px, 50px);
        }
        50% {
          transform: translate(0px, 0px);
        }
        70% {
          transform: translate(0px, 0px);
        }
        100% {
          transform: translate(-42px, 0px);
        }
      }
    }
    div {
      margin-left: 1.5rem;
      opacity: 0;
      animation: fade-in;
      animation-duration: 200ms;
      animation-delay: 900ms;
      animation-fill-mode: forwards;
      @keyframes fade-in {
        0% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    }
  }
  .default {
    span {
      padding-top: 0.25rem;
      transform: translate(-42px, 0px);
      position: absolute;
    }
    div {
      margin-left: 1.5rem;
    }
  }
`;

const MarkComplete: React.FC<CardMenuProps> = ({
  courseData,
  lessonData,
  markCompleteData = {},
  courseId: courseIdProp,
  testId,
  onlyIcons,
  isCourseHeader,
  isSong,
}) => {
  const [isClicked, setIsClicked] = useState(false);
  const dispatch = useDispatch();
  const { user } = usePlayUser();
  const selector = (state: RootState) => ({
    coursesProgress: state.myProgress.courses,
    lessonProgress: state.myProgress.lessons,
  });
  const { coursesProgress, lessonProgress } = useSelector(selector);
  const { id: courseId = '', slug: courseSlug } = courseData || {};
  const { id: lessonId } = lessonData || {};
  const url = window.location.href;
  const isCourseDetail = url.indexOf('/course/') > -1 && url.indexOf('/lesson/') < 0;
  const isTabletUp = useBreakpoint(up('tablet-portrait'));

  const isComplete = ((): boolean => {
    if (courseData && !lessonData) {
      const { lessons } = courseData || {};
      const courseIsMarkedComplete = coursesProgress[courseId]?.completed ?? false;
      return courseIsMarkedComplete || getPercentCompleted(lessons, lessonProgress) === 1;
    }
    if (lessonData) {
      const lessonPercent = getLessonPercent(lessonId, lessonProgress);
      return lessonPercent === 1;
    }
    return false;
  })();

  const markCourseComplete = (): void => {
    if (!courseData) return;
    const { lessons } = courseData;
    const lessonsToMarkComplete = getLessonsPayload(lessons, !isComplete);
    const courseUpdateData = { courseId, completed: !isComplete };
    dispatch(updateLessonCompletedProgress(lessonsToMarkComplete, courseUpdateData));
  };

  const markLessonComplete = (): void => {
    if (!lessonData) return;
    const lessonsToMarkComplete = getLessonsPayload([lessonData], !isComplete);
    const courseUpdateData = isComplete ? { courseId: courseIdProp, completed: !isComplete } : null;
    dispatch(updateLessonCompletedProgress(lessonsToMarkComplete, courseUpdateData));
  };

  const dispatchSegmentEvent = (): void => {
    const isOnPath = isCourseDetail ? 'course-detail' : 'my-path';
    const { title: courseTitle, lessons: courseLessons } = courseData || {};
    const { level: lessonLevel, type: lessonType, slug: lessonSlug, title: lessonName } =
      lessonData || {};

    /* eslint-disable @typescript-eslint/camelcase */
    const globalProperties = {
      fc_id: user?.userID,
      is_on_path: isOnPath,
      genre: markCompleteData?.style,
      path_id: markCompleteData['path-id'],
      playlist_id: markCompleteData['playlist-id'],
      path: capitalize(markCompleteData?.style),
      playlist: markCompleteData?.title,
    };

    const courseLevel = courseLessons?.[0]?.['level'];
    const instrument =
      courseLessons?.[0]?.instrument ?? markCompleteData?.instrument ?? lessonData?.instrument;

    const courseProperties = {
      course_id: courseId,
      course_slug: courseSlug,
      course_name: courseTitle,
      instrument,
      level: courseLevel,
      playlist: `${markCompleteData?.style} level ${courseLevel}`,
    };

    const trackingProperties = isCourseDetail
      ? {
          ...globalProperties,
          ...courseProperties,
          lesson_id: lessonId,
          lesson_slug: lessonSlug,
          lesson_type_1: lessonType,
          lesson_name: lessonName,
          video_id: lessonData?.['activity-lesson-video'].id,
          instrument,
          level: lessonLevel ?? courseLevel,
        }
      : {
          ...globalProperties,
          ...courseProperties,
        };

    const legacyProperties = {
      user_id: user?.userID,
      play_list_id: markCompleteData['playlist-id'],
    };

    /* eslint-enable @typescript-eslint/camelcase */
    trackEvent(
      !isComplete ? EventNames.COURSE_MARKED_AS_COMPLETE : EventNames.COURSE_UNMARKED_AS_COMPLETE,
      trackingProperties,
      legacyProperties,
    );
  };

  const onConfirm = (): void => {
    if (courseData && !lessonData) markCourseComplete();
    if (lessonData) markLessonComplete();
    dispatchSegmentEvent();
  };

  const handleClick = (event: SyntheticEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    onConfirm();
    if (!isComplete) {
      setIsClicked(true);
    }
  };

  const Icons = (
    <IconButton
      onClick={handleClick}
      data-id={`button-mark-as-complete-${testId}`}
      aria-label="Mark complete"
      aria-pressed={isComplete}
    >
      {isComplete ? (
        <CompletedIcon width="25px" height="25px" aria-hidden="true" />
      ) : (
        <IncompleteIcon width="25px" height="25px" aria-hidden="true" />
      )}
    </IconButton>
  );

  const FullButton = (
    <ButtonContainer>
      <Button
        isSong={isSong}
        isComplete={isComplete}
        onClick={handleClick}
        aria-label="Mark complete"
        aria-pressed={isComplete}
        data-id={`button-mark-as-complete-${testId}`}
        className={`default ${isClicked && 'complete'}`}
      >
        {isComplete && (
          <span>
            <FreemiumCheckMark fill="white" height="1rem" width="1rem" aria-hidden="true" />
            <Spacer spacing="sm3" direction="vertical" />
          </span>
        )}
        {isComplete ? <div>Completed</div> : 'Mark as complete'}
      </Button>
    </ButtonContainer>
  );

  if (onlyIcons) return Icons;
  if (isCourseHeader) return isTabletUp ? FullButton : Icons;
  return FullButton;
};

export default MarkComplete;
